import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { firmwareList } from "../../actions/doctorActions";
import Table from "../react-table-component/ReactTable";
import { formatDate } from "../../helper";
import { Link } from "react-router-dom";
import { DropDownItem } from "../header/Header";
import historyIcon from "../../assets/img/svg/history-icon.svg";
import editIcon from "../../assets/img/svg/edit-icon.svg";
import dotMenuIcon from "../../assets/img/svg/dots-menu-icon.svg";
import trashIcon from "../../assets/img/svg/trash-can-2.svg";
import downloadIcon from "../../assets/img/svg/download.svg";
import { FirmwareDeleteConfirmModal } from "./FirmwareDeleteConfirmModal";

export default function FirmwareList() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginData.token);
  const { loading, firmware } = useSelector((state) => state.firmwareList);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [id, setId] = useState(null);

  const [searchField, setSearchField] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const simulateProgress = () => {
      let interval = setInterval(() => {
        setProgress((prev) => (prev >= 100 ? 100 : prev + 20));
      }, 400);
      return () => clearInterval(interval);
    };
    simulateProgress();
    dispatch(firmwareList(token)).finally(() => setProgress(100));
  }, [dispatch, token]);

  const handleSearch = (e) => setSearchField(e.target.value.toLowerCase());

  const filteredFirmware = React.useMemo(() => {
    return (
      firmware?.filter((item) =>
        item.firmware_version?.toLowerCase()?.includes(searchField),
      ) || []
    );
  }, [firmware, searchField]);

  const dotMenuRef = useRef(null);
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState(null);
  const toggleMenuForRow = (rowIndex) => {
    setOpenMenuRowIndex((prevRowIndex) =>
      prevRowIndex === rowIndex ? null : rowIndex,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dotMenuRef.current &&
        !dotMenuRef.current.contains(event.target) &&
        openMenuRowIndex !== null
      ) {
        setOpenMenuRowIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuRowIndex]);

  const handleDownload = (fw) => {
    window.open(fw.path_to_folder, "_blank");
  };

  const columns = React.useMemo(() => [
    { Header: "Firmware Version", accessor: "firmware_version" },
    {
      Header: "Date Uploaded",
      accessor: "date_uploaded",
      Cell: ({ value }) => (value ? formatDate(value) : "N/A"),
    },
    {
      Header: "Changelog",
      accessor: "changelog",
      Cell: ({ value }) =>
        value && value.length > 60
          ? `${value.slice(0, 60)}...`
          : value || "N/A",
    },
    { Header: "File Size (Bytes)", accessor: "file_size" },
    { Header: "IOS Version", accessor: "version" },
    {
      Header: "",
      accessor: "action",
      disableSortBy: true,
      Cell: ({ row }) => {
        const isMenuOpen = openMenuRowIndex === row.index;
        return (
          <div className="dot-menu-icon">
            <img
              src={dotMenuIcon}
              alt="dot menu"
              onClick={() => toggleMenuForRow(row.index)}
            />

            {isMenuOpen && (
              <div ref={dotMenuRef} className="patients-dot-menu">
                <DropDownItem
                  item={{
                    title: "View",
                    icon: historyIcon,
                    to: `/firmware/${row?.original?.id}`,
                  }}
                />
                <DropDownItem
                  item={{
                    title: "Edit",
                    icon: editIcon,
                    to: `/firmware-update/${row?.original?.id}`,
                  }}
                />
                <DropDownItem
                  item={{
                    title: "Download",
                    icon: downloadIcon,
                  }}
                  handleClick={() => handleDownload(row?.original)}
                />
                <div className="delete-clr">
                  <DropDownItem
                    item={{
                      title: "Delete",
                      icon: trashIcon,
                    }}
                    handleClick={() => {
                      setId(row?.original?.id);
                      setShowDeleteDialog(true);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      },
    },
  ]);

  return (
    <div className="patient-list">
      <FirmwareDeleteConfirmModal
        show={showDeleteDialog}
        handleClose={() => {
          setShowDeleteDialog(false);
        }}
        id={id}
        token={token}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <h3 style={{ margin: 0 }}>Firmware List</h3>
        <Link to={`/firmware-create`}>
          <button
            className="button btn-default"
            style={{
              padding: "0.35rem 0.7rem",
              fontSize: "0.9rem",
              fontWeight: "bold",
            }}
          >
            Add Firmware
          </button>
        </Link>
      </div>

      <Table
        columns={columns}
        data={filteredFirmware}
        loading={loading}
        tableOf="Firmware"
        progress={progress}
      />
    </div>
  );
}
